import { Logtail } from "@logtail/browser";
import debugModule from "debug";

import { isPlainObject, uuid } from "./utils";

const logtailKey = {
  production: "utjXFUTVaHU7JvdXuqewYTt3",
  staging: "dMTFj4WskYWBw4H1LzJnsqU4",
}[process.env.NODE_ENV ?? "development"];

const logtailEndpoint = {
  production: "https://log-intake-production.helloscreen.com/",
  staging: "https://log-intake-staging.helloscreen.xyz/",
}[process.env.NODE_ENV ?? "development"];

const pageviewId = uuid();
let context: Record<string, any> = {
  pageviewId,
  release: process.env.CURRENT_APP_VERSION,
};

const logtail = logtailKey
  ? new Logtail(logtailKey, {
      burstProtectionMax: 250,
      burstProtectionMilliseconds: 5_000,
      endpoint: logtailEndpoint,
      syncMax: 1,
    })
  : null;

export default function logger(
  namespace: string,
  throttle: { max: number; seconds: number } = { max: 30, seconds: 5 },
) {
  const debug = debugModule(`upscope:${namespace}`);
  const debugWarning = debugModule(`upscope:${namespace}:warn`);

  let currentThrottle = 0;

  function isThrottled() {
    if (currentThrottle === 0) {
      setTimeout(() => {
        if (currentThrottle > throttle.max)
          logtail?.warn(`Dropped ${currentThrottle - throttle.max} logs due to throttle`, { ...context, namespace });

        currentThrottle = 0;
      }, throttle.seconds * 1000);
    }

    return currentThrottle++ > throttle.max;
  }

  const safeJsonStringify = (obj: any): string => {
    if (Array.isArray(obj)) return `[ ${obj.map(safeJsonStringify).join(" , ")} ]`;
    if (!isPlainObject(obj)) return String(obj);
    try {
      return JSON.stringify(obj);
      // eslint-disable-next-line upscope/require-proper-error-handling
    } catch (e) {
      return `Unserializable object: ${e}`;
    }
  };
  const func = function (message: string, ...args: any[]) {
    debug(message, ...args);
    if (isThrottled()) return;

    args = args.map((arg) => safeJsonStringify(arg).slice(0, 1000));
    const logArgs = args.slice();
    logtail?.info(
      message.replace(/%[sodO]/g, () => logArgs.shift().slice(0, 30)),
      {
        arguments: args,
        namespace,
        ...context,
      },
    );
  };

  func.local = function (message: string, ...args: any[]) {
    debug(message, ...args);
  };

  func.warning = function (message: string, ...args: any[]) {
    debugWarning(message, ...args);
    if (isThrottled()) return;

    args = args.map((arg) => safeJsonStringify(arg).slice(0, 200));
    const logArgs = args.slice();
    logtail?.warn(
      message.replace(/%[sodO]/g, () => logArgs.shift().slice(0, 30)),
      {
        arguments: args,
        namespace: `${namespace}:warn`,
        ...context,
      },
    );
  };

  return func;
}

export function setLoggerContext(newContext: Record<string, any>) {
  context = { ...context, ...newContext };
}
